import React, {PureComponent} from "react";
import RewardEntity from "../../data/entities/RewardEntity";
import {ScaleLoader} from "react-spinners";
import {Col, Row} from "reactstrap";
import Reward from "../../components/Rewards/Reward";
import {GameEntity} from "../../data/entities/GameEntity";
import CustomerOffersService from "../../data/services/CustomerOffersService";
import axios, {AxiosError, AxiosResponse, CancelTokenSource} from "axios";
import {UserContext} from "../../context/UserContext";
import {FormattedMessage} from "react-intl";
import _ from "underscore";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import RewardsService from "../../data/services/RewardsService";
import {OverlayScrollbarsComponent} from "overlayscrollbars-react";
import ErrorMessage from "../../components/ErrorMessage";

interface Props {
    rewards: RewardEntity[];
    games: GameEntity[];
    ruleId: number;
    locale: string;
    period: number;
    rewardsLeft: number;
    claimReward: (index: number, callback: (success: boolean) => void) => void;
}

interface State {
    loading: boolean;
    hasError: boolean;
    rewards: RewardEntity[];
    paginated: boolean;
    pageItems: number;
    currentPage: number;
}

export default class RewardsList extends PureComponent<Props, State> {
    static contextType = UserContext;
    context!: React.ContextType<typeof UserContext>;

    private requestCancelTokenSource: CancelTokenSource | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            loading: false,
            hasError: false,
            rewards: this.props.rewards,
            paginated: this.shouldPaginate(),
            pageItems: this.rewardsPerPage(),
            currentPage: 1,
        };
    }

    componentDidMount(): void {
        this.paginateRewards();
        window.addEventListener('resize', this.paginateRewardsDebounced);
    }

    componentWillUnmount(): void {
        window.removeEventListener('resize', this.paginateRewardsDebounced);
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (this.props.period !== prevProps.period) {
            this.switchOffer();
        }

        if (this.props.rewards !== prevProps.rewards) {
            this.setState({rewards: this.props.rewards});
        }
    }

    render(): JSX.Element {
        if (this.state.loading) {
            return (
                <div className="rewards-list-loader">
                    <ScaleLoader color="#fff"/>
                </div>
            );
        }

        let {paginated, currentPage, pageItems, rewards} = this.state;

        return (
            <>
                {
                    !this.state.hasError &&
                    <>
                        <OverlayScrollbarsComponent options={{scrollbars: {snapHandle: true, clickScrolling: true}}}>
                            <Row className="rewards-list">
                                {this.rewards()}
                            </Row>
                        </OverlayScrollbarsComponent>

                        {
                            (paginated && (currentPage * pageItems) < rewards.length) &&
                            <Row className="loadmore">
                                <Col>
                                    <button onClick={this.loadMoreRewards}>
                                        <FormattedMessage id="rewards.load_more"/>
                                    </button>
                                </Col>
                            </Row>
                        }

                    </>
                }

                {
                    this.state.hasError &&
                        <ErrorMessage
                            message={<FormattedMessage id="message.error"/>}
                            onRefresh={this.switchOffer}
                        />
                }
            </>
        );
    }

    rewards = (): JSX.Element => {
        if (this.state.rewards.length === 0) {
            return (
                <Col>
                    <p className="my-5">
                        <FormattedMessage id="rewards.none_found"/>
                    </p>
                </Col>
            );
        } else {
            let {paginated, currentPage, pageItems} = this.state;
            let rewards: RewardEntity[] = paginated ? this.state.rewards.slice(0, currentPage * pageItems) : this.state.rewards;

            return (
                <TransitionGroup component={null}>
                    {
                        rewards.map((reward, index) => (
                            <CSSTransition
                                key={index}
                                timeout={300}
                                classNames="move"
                                exit={false}
                            >
                                <Reward
                                    reward={reward}
                                    games={_.indexBy(this.props.games, "gameId")}
                                    period={this.props.period}
                                    rewardsLeft={this.props.rewardsLeft}
                                    claimReward={this.props.claimReward}
                                    index={index}
                                />
                            </CSSTransition>
                        ))
                    }
                </TransitionGroup>
            );
        }
    };

    switchOffer = (): void => {
        if (this.requestCancelTokenSource) {
            this.requestCancelTokenSource.cancel();
        }

        this.requestCancelTokenSource = axios.CancelToken.source();

        this.setState({loading: true, hasError: false}, () => {
            if (this.props.period < 0) {
                this.fetchHistory()
            } else {
                this.fetchOffer();
            }
        });
    };

    fetchHistory = (): void => {
        if (this.context.user.ticketData.ticket) {
            RewardsService.getRewardsHistory(
                this.context.user.ticketData.ticket,
                this.props.locale,
                this.props.ruleId,
                this.requestCancelTokenSource ? this.requestCancelTokenSource.token : undefined
            ).then((response: AxiosResponse) => {
                this.setState({
                    rewards: response.data.rewards,
                    currentPage: 1,
                    loading: false
                });
            }).catch((error: AxiosError) => {
                if (!axios.isCancel(error)) {
                    this.setState({
                        hasError: true,
                        loading: false
                    });
                }
            });
        }
    };

    fetchOffer = (): void => {
        let period: "previous" | "current" | "upcoming" = "current";

        if (this.props.period < 0) {
            period = "previous";
        } else if (this.props.period > 0) {
            period = "upcoming";
        }

        if (this.context.user.ticketData.ticket) {
            CustomerOffersService.getCustomerOffer(
                this.context.user.ticketData.ticket,
                this.props.locale,
                period,
                this.props.ruleId,
                this.requestCancelTokenSource ? this.requestCancelTokenSource.token : undefined
            ).then((response: AxiosResponse) => {
                this.setState({
                    rewards: (response.data.offers.length === 0 ? [] : response.data.offers[0].rewards),
                    currentPage: 1,
                    loading: false
                });
            }).catch((error: AxiosError) => {
                if (!axios.isCancel(error)) {
                    this.setState({
                        hasError: true,
                        loading: false
                    });
                }
            });
        }
    };

    private shouldPaginate = (): boolean => {
        return window.innerWidth > 575;
    };

    private rewardsPerPage = (): number => {
        if (window.innerWidth < 992) {
            return 2;
        } else if (window.innerWidth < 1200) {
            return 3;
        } else {
            return 4;
        }
    };

    private paginateRewards = (): void => {
        let shouldPaginate: boolean = this.shouldPaginate();
        let rewardsPerPage: number = this.rewardsPerPage();

        if (this.state.paginated !== shouldPaginate || this.state.pageItems !== rewardsPerPage) {
            this.setState({paginated: shouldPaginate, pageItems: rewardsPerPage});
        }
    };

    private paginateRewardsDebounced = _.debounce(this.paginateRewards, 300);

    loadMoreRewards = (): void => {
        this.setState(prevState => ({
            currentPage: prevState.currentPage + 1
        }));
    };
}